import type { FC } from 'react';
import { useCallback } from 'react';
import { useSetAtom } from 'jotai';

import { FilterProvider } from 'src/components/app/context/FilterContext';
import { GuidedTourContextProvider } from 'src/components/app/context/GuidedTourContext';
import { useSection } from 'src/components/app/context/SectionContext';
import type { PageFrameProps } from 'src/components/common/PageFrame';
import PageFrame from 'src/components/common/PageFrame';
import PageNotFound from 'src/components/common/PageNotFound';
import LoadingPage from 'src/components/app/LoadingPage';
import { useDashboardContext } from 'src/components/dashboard/DashboardContext';
import { dashboardHiddenSections } from 'src/components/dashboard/DashboardPage/atom';
import { revealHiddenTarget } from 'src/components/dashboard/DashboardPage/components/WidgetWrapper/helper';
import useComparisonMode from 'src/components/dashboard/hooks/useComparisonMode';
import useInitialFilterGroups from 'src/components/followUp/hooks/useInitialFilterGroups';
import TicketFilterProvider from 'src/components/followUp/TicketFilterContext';

import { DashboardPage } from './DashboardPage';
import { GlobalDashboardPage } from './GlobalDashboardPage';

export const DASHBOARD_PAGE_LOADING_INDICATOR_TEST_ID =
  'dashboard-page-loading-indicator';

export interface DashboardPageProps extends PageFrameProps {}

// Wrap the page in a separate FilterProvider as its call of the useOrgTreeState-hook would run into racing conditions
// with the global initialization of the PlanActionContext and its call of the useOrgTreeState-hook.
const DashboardPageWrapper: FC<DashboardPageProps> = ({ ...props }) => {
  const { dashboardConfig, filters, loading } = useDashboardContext();
  const { sectionIsLoaded } = useSection();
  const {
    allow_combined_filters,
    id: dashboardId,
    is_global_dashboard: isGlobal,
  } = dashboardConfig || {};

  const setHiddenSection = useSetAtom(dashboardHiddenSections);
  const revealHiddenSections = useCallback(
    revealHiddenTarget(setHiddenSection),
    [setHiddenSection],
  );
  const comparison = useComparisonMode();
  const { initialFilterGroups, initialFilterSpecs } = useInitialFilterGroups({
    dateRange: !!filters.rangeFilter,
    comparison,
  });

  // Initial filter props are consumed only when the provider mounts.
  if (!sectionIsLoaded || (loading && !dashboardConfig)) {
    return <LoadingPage />;
  }

  if (!dashboardConfig) {
    return (
      <PageFrame {...props}>
        <PageNotFound
          headlineKey="pageNotFound.accessDenied"
          explanationKey="pageNotFound.shortExplanation"
        />
      </PageFrame>
    );
  }

  return (
    <FilterProvider
      allowCombinedFilters={!!allow_combined_filters}
      initialFilterGroups={initialFilterGroups}
      initialFilterSpecs={initialFilterSpecs}
      key={dashboardId}
    >
      <TicketFilterProvider disableListQuery>
        <GuidedTourContextProvider stepAfterHook={revealHiddenSections}>
          {isGlobal ? (
            <GlobalDashboardPage {...props} />
          ) : (
            <DashboardPage {...props} />
          )}
        </GuidedTourContextProvider>
      </TicketFilterProvider>
    </FilterProvider>
  );
};

export default DashboardPageWrapper;
